import { EnvConfig, Media, MediaData } from '@gripp/shared-logic';
import { useEffect, useRef, useState } from 'react';
import { createTypedMediaItem, processTypedMediaItem } from './mediaUtils';

export const useTypedMediaItem = <T extends MediaData>(
  existingMediaItem: Media | undefined
) => {
  const [typedMediaItem, setTypedMediaItem] = useState<T | undefined>();
  const baseMediaUrl = EnvConfig.mediaUrl;
  const baseApiUrl = EnvConfig.apiUrl;
  const existingMediaItemProcessed = useRef(false);
  // Tracks the id of the last `existingMediaItem` we derived from, so we can
  // tell "an existing item was removed" apart from "there was never one".
  const lastExistingMediaIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (existingMediaItem) {
      const isNewItem =
        !existingMediaItemProcessed.current ||
        typedMediaItem?.id !== existingMediaItem.id;
      const statusChanged =
        typedMediaItem?.id === existingMediaItem.id &&
        typedMediaItem?.status !== existingMediaItem.status;

      if (isNewItem || statusChanged) {
        setTypedMediaItem(
          createTypedMediaItem(existingMediaItem, baseMediaUrl, baseApiUrl) as T
        );
        existingMediaItemProcessed.current = true;
      }
      lastExistingMediaIdRef.current = existingMediaItem.id;
    } else if (lastExistingMediaIdRef.current !== undefined) {
      // We previously derived a typed item from a real `existingMediaItem` and
      // now it's gone. This happens when a list row component is reconciled in
      // place onto a different record whose media is absent (e.g. the activity
      // feed recycles an IssueCard from an asset with a cover image onto one
      // without). Clear the stale item so we don't render the previous record's
      // image. Note we only clear here — if there was never an existing item
      // (ref still undefined) we leave state alone so imperative `handleSet`
      // uploads (singleImageInput/addMedia) survive re-renders.
      setTypedMediaItem(undefined);
      existingMediaItemProcessed.current = false;
      lastExistingMediaIdRef.current = undefined;
    }
  }, [existingMediaItem, existingMediaItem?.id, existingMediaItem?.status, baseMediaUrl, baseApiUrl]);

  const handleSet = (
    newMediaItem: T,
    callback?: (newMediaItem: Media) => void
  ) => {
    processTypedMediaItem(newMediaItem);
    setTypedMediaItem(() => {
      callback?.(newMediaItem);
      return newMediaItem;
    });
  };

  const handleUpdate = (updatedMediaItem: T) => {
    setTypedMediaItem((prevState: T | undefined) =>
      prevState && prevState.id === updatedMediaItem.id
        ? { ...prevState, ...updatedMediaItem }
        : prevState
    );
  };

  const handleUploadProgress = (mediaId: string, progress: number) => {
    const toSetProgress = {
      id: mediaId,
      uploadProgress: progress,
      isUploading: progress !== 1,
    } as T;
    handleUpdate(toSetProgress);
  };

  const handleRefreshMediaItem = (media?: T) => {
    setTypedMediaItem(media);
  };

  return {
    typedMediaItem,
    handleSet,
    handleUploadProgress,
    handleUpdate,
    handleRefreshMediaItem,
  };
};
